import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { ArrowUpDown, Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

// Every calendar date from pickup through delivery, inclusive.
function loadDateSpan(load) {
  if (!load.pickup_date || !load.delivery_date) return [];
  const days = [];
  const d = new Date(`${load.pickup_date}T00:00:00`);
  const endD = new Date(`${load.delivery_date}T00:00:00`);
  if (endD < d) return [load.pickup_date];
  while (d <= endD) {
    days.push(d.toISOString().split('T')[0]);
    d.setDate(d.getDate() + 1);
  }
  return days;
}

// Days a driver was actually "out" on a load -- deduped per driver via a
// Set (so two back-to-back or overlapping loads don't double-count a day)
// and clipped to the selected date range.
export default function OTRDaysReport() {
  const [loads, setLoads] = useState([]);
  const [assets, setAssets] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('OTR Days');
  const [sortDir, setSortDir] = useState(-1);
  const company = useCompanyInfo();

  useEffect(() => {
    Promise.all([
      client.entities.Load.list('-delivery_date', 500),
      client.entities.Asset.filter({ asset_type: 'Truck' }, 'unit_number', 200),
      client.entities.DriverProfile.list('full_name', 100),
    ]).then(([l, a, d]) => { setLoads(l); setAssets(a); setDrivers(d); setLoading(false); });
  }, []);

  const relevantLoads = loads.filter(l => l.assigned_driver_email && l.pickup_date && l.delivery_date);

  const daysByDriver = {};
  relevantLoads.forEach(l => {
    const span = loadDateSpan(l).filter(day => inRange(day, start, end));
    if (span.length === 0) return;
    if (!daysByDriver[l.assigned_driver_email]) daysByDriver[l.assigned_driver_email] = { days: new Set(), loads: 0, truckUnit: null };
    span.forEach(day => daysByDriver[l.assigned_driver_email].days.add(day));
    daysByDriver[l.assigned_driver_email].loads += 1;
    // Loads are fetched newest-delivery-first, so the first load carrying
    // a truck_unit snapshot for this driver is their most recent one --
    // a better label than "whichever truck this driver is CURRENTLY on,"
    // which can be wrong by report time if they've since been reassigned.
    if (!daysByDriver[l.assigned_driver_email].truckUnit && l.truck_unit) {
      daysByDriver[l.assigned_driver_email].truckUnit = l.truck_unit;
    }
  });

  let rows = Object.entries(daysByDriver).map(([email, v]) => {
    const driver = drivers.find(d => d.user_email === email);
    const unit = v.truckUnit || assets.find(a => a.assigned_driver_email === email)?.unit_number;
    return {
      Driver: driver?.full_name || email,
      'Unit #': unit || '—',
      Loads: v.loads,
      'OTR Days': v.days.size,
      email,
    };
  });

  rows.sort((a, b) => sortDir * ((b[sortKey] ?? 0) - (a[sortKey] ?? 0)));

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d * -1);
    else { setSortKey(key); setSortDir(-1); }
  };

  const csvRows = rows.map(({ email: _email, ...r }) => r);

  const handlePrint = () => {
    const cols = ['Driver', 'Unit #', 'Loads', 'OTR Days'];
    const dataRows = rows.map(r => [{ value: r.Driver, class: 'bold' }, r['Unit #'], r.Loads, { value: r['OTR Days'], class: 'gold' }]);
    const cards = [
      { label: 'Drivers', value: rows.length, color: 'gold' },
      { label: 'Avg OTR Days', value: rows.length ? (rows.reduce((s, r) => s + r['OTR Days'], 0) / rows.length).toFixed(1) : '0', color: '' },
    ];
    const html = generatePrintHTML('Over The Road Days Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, null, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'otr-days.csv')} exportDisabled={loading || rows.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Driver', 'Unit #', 'Loads', 'OTR Days'].map(h => (
                <th key={h} onClick={() => toggleSort(h)} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap cursor-pointer hover:text-foreground select-none">
                  <span className="flex items-center gap-1">{h} <ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r.email} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-foreground">{r.Driver}</td>
                <td className="px-3 py-2.5 text-primary">{r['Unit #']}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r.Loads}</td>
                <td className="px-3 py-2.5 font-bold text-primary">{r['OTR Days']}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No OTR data for this period.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
      <p className="text-xs text-muted-foreground">OTR Days = distinct calendar days from pickup through delivery across all of a driver's loads, deduplicated so overlapping/back-to-back loads don't double-count a day.</p>
    </div>
  );
}
